import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, worksheetApi, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';
import '../styles/admin-dashboard.css';

const FORMAT_BADGES = {
  pdf: { label: 'PDF Document', color: '#e11d48', bg: '#ffe4e6', icon: '📄' },
  doc: { label: 'Word Doc', color: '#2563eb', bg: '#dbeafe', icon: '📝' },
  docx: { label: 'Word Docx', color: '#2563eb', bg: '#dbeafe', icon: '📝' },
  xlsx: { label: 'Excel Sheet', color: '#059669', bg: '#d1fae5', icon: '📊' },
  xls: { label: 'Excel Sheet', color: '#059669', bg: '#d1fae5', icon: '📊' },
  image: { label: 'Image / Diagram', color: '#7c3aed', bg: '#ede9fe', icon: '🖼️' },
  other: { label: 'Document', color: '#4b5563', bg: '#f3f4f6', icon: '📑' },
};

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function AdminWorksheetsPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, totalDownloads: 0 });

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  // Form Data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    references: '',
    fileUrl: '',
    fileName: '',
    fileSize: 0,
    fileType: 'pdf',
    isPublished: true,
  });

  // Preview / Details Modal State
  const [previewItem, setPreviewItem] = useState(null);

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // Toast Feedback State
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  const fetchWorksheets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await worksheetApi.adminGetWorksheets({
        search: searchQuery,
        status: statusFilter,
        sortBy,
      });

      if (res?.success) {
        setWorksheets(res.worksheets || []);
        if (res.stats) {
          setStats(res.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching worksheets:', err);
      showFeedback('error', err.message || 'Failed to load worksheets');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, sortBy]);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchWorksheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorksheets, authSession?.token, navigate]);

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setModalError('');
    setFormData({
      title: '',
      description: '',
      references: '',
      fileUrl: '',
      fileName: '',
      fileSize: 0,
      fileType: 'pdf',
      isPublished: true,
    });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item) => {
    setModalMode('edit');
    setEditingId(item._id || item.id);
    setModalError('');
    setFormData({
      title: item.title || '',
      description: item.description || '',
      references: item.references || '',
      fileUrl: item.fileUrl || '',
      fileName: item.fileName || '',
      fileSize: item.fileSize || 0,
      fileType: item.fileType || 'pdf',
      isPublished: item.isPublished !== undefined ? item.isPublished : true,
    });
    setIsModalOpen(true);
  };

  // Submit Create or Edit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!formData.title.trim()) {
      setModalError('Please enter a worksheet title.');
      return;
    }

    let currentFileUrl = formData.fileUrl?.trim() || '';
    let currentFileName = formData.fileName || '';
    let currentFileSize = formData.fileSize || 0;
    let currentFileType = formData.fileType || 'pdf';

    try {
      setFormSubmitting(true);

      const payload = {
        ...formData,
        fileUrl: currentFileUrl,
        fileName: currentFileName,
        fileSize: currentFileSize,
        fileType: currentFileType,
      };

      if (modalMode === 'create') {
        const res = await worksheetApi.adminCreateWorksheet(payload);
        if (res?.success) {
          showFeedback('success', 'Worksheet added and published to registered therapists!');
          setIsModalOpen(false);
          fetchWorksheets();
        } else {
          throw new Error(res?.message || 'Failed to save worksheet');
        }
      } else {
        const res = await worksheetApi.adminUpdateWorksheet(editingId, payload);
        if (res?.success) {
          showFeedback('success', 'Worksheet updated successfully!');
          setIsModalOpen(false);
          fetchWorksheets();
        } else {
          throw new Error(res?.message || 'Failed to update worksheet');
        }
      }
    } catch (err) {
      console.error('Save worksheet error:', err);
      const errMsg = err.message || 'Failed to save worksheet.';
      setModalError(errMsg);
      showFeedback('error', errMsg);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Toggle Publish Status
  const handleTogglePublish = async (item) => {
    const id = item._id || item.id;
    try {
      // Optimistic update
      setWorksheets((prev) =>
        prev.map((w) => ((w._id === id || w.id === id) ? { ...w, isPublished: !w.isPublished } : w))
      );

      const res = await worksheetApi.adminTogglePublish(id);
      if (res?.success) {
        showFeedback(
          'success',
          `Worksheet ${res.isPublished ? 'is now visible to all registered therapists' : 'is now hidden (draft)'}`
        );
        fetchWorksheets();
      }
    } catch (err) {
      console.error('Toggle error:', err);
      showFeedback('error', err.message || 'Failed to update status');
      fetchWorksheets();
    }
  };

  // Delete Handler
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;
    const id = deleteModal.item._id || deleteModal.item.id;

    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      const res = await worksheetApi.adminDeleteWorksheet(id);
      if (res?.success) {
        showFeedback('success', 'Worksheet deleted successfully.');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchWorksheets();
      }
    } catch (err) {
      console.error('Delete error:', err);
      showFeedback('error', err.message || 'Failed to delete worksheet');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Filtered in-memory list if needed
  const filteredList = worksheets.filter((w) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !searchQuery ||
      w.title.toLowerCase().includes(q) ||
      (w.description && w.description.toLowerCase().includes(q)) ||
      (w.references && w.references.toLowerCase().includes(q)) ||
      (w.fileName && w.fileName.toLowerCase().includes(q));
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'published' && w.isPublished) ||
      (statusFilter === 'draft' && !w.isPublished);
    return matchSearch && matchStatus;
  });

  // Pagination slicing
  const totalPages = Math.ceil(filteredList.length / itemsPerPage) || 1;
  const paginatedList = filteredList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getFormatBadge = (type = 'pdf') => {
    const clean = String(type).toLowerCase().replace('.', '');
    return FORMAT_BADGES[clean] || FORMAT_BADGES.other;
  };

  return (
    <div className="admin-dashboard">
      <DashboardSidebar userRole="admin" />

      <main className="admin-dashboard__main" style={{ marginLeft: '280px', padding: '30px', minHeight: '100vh', background: '#f8fafc' }}>
        {/* Top Feedback Notification */}
        {feedback.message && (
          <div
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              zIndex: 9999,
              padding: '16px 24px',
              borderRadius: '12px',
              backgroundColor: feedback.type === 'error' ? '#ef4444' : '#10b981',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '15px',
              fontWeight: 600,
              animation: 'fadeIn 0.3s ease',
            }}
          >
            <span>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
            <span>{feedback.message}</span>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', marginLeft: '10px' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Header Title Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '32px' }}>📑</span>
              <div>
                <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: '#1e293b' }}>
                  Clinical Worksheets Management
                </h1>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                  Upload and manage clinical worksheets, assessment tools, and therapy exercises accessible to all registered therapists.
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate('/admin/worksheet-forms')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffffff',
                color: '#4f46e5',
                border: '1px solid #c7d2fe',
                padding: '12px 18px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                transition: 'all 0.2s',
              }}
            >
              <span>📋</span>
              <span>Dynamic Form Builder</span>
            </button>

            <button
              onClick={handleOpenCreateModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 22px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <span>➕</span>
              <span>Add New Worksheet</span>
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '25px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
              <span>TOTAL WORKSHEETS</span>
              <span style={{ fontSize: '20px' }}>📚</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', marginTop: '10px' }}>
              {stats.total || worksheets.length}
            </div>
            <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px', fontWeight: 500 }}>
              Available for Therapist Download
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
              <span>ACTIVE & VISIBLE TO THERAPISTS</span>
              <span style={{ fontSize: '20px' }}>🌐</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#10b981', marginTop: '10px' }}>
              {stats.published || worksheets.filter((w) => w.isPublished).length}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              Instant download ready
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
              <span>DRAFTS / UNPUBLISHED</span>
              <span style={{ fontSize: '20px' }}>🔒</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#f59e0b', marginTop: '10px' }}>
              {stats.drafts !== undefined ? stats.drafts : worksheets.filter((w) => !w.isPublished).length}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              Hidden from therapist view
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
              <span>THERAPIST DOWNLOADS</span>
              <span style={{ fontSize: '20px' }}>📥</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#6366f1', marginTop: '10px' }}>
              {stats.totalDownloads || worksheets.reduce((acc, w) => acc + (w.downloadCount || 0), 0)}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              Clinical usage counter
            </div>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div
          style={{
            background: 'white',
            padding: '18px 22px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            marginBottom: '25px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', flex: 1, minWidth: '280px' }}>
            {/* Search */}
            <div style={{ position: 'relative', minWidth: '260px', flex: 1 }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Search worksheets by name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 38px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: 'white',
                color: '#334155',
                outline: 'none',
              }}
            >
              <option value="all">All Statuses</option>
              <option value="published">🟢 Published (Visible)</option>
              <option value="draft">🟡 Draft (Hidden)</option>
            </select>

            {/* Sort Filter */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: 'white',
                color: '#334155',
                outline: 'none',
              }}
            >
              <option value="newest">Newest First</option>
              <option value="downloads">Most Downloaded</option>
              <option value="title">Alphabetical (A-Z)</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>

          {/* View Mode Toggle */}
          <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                background: viewMode === 'grid' ? 'white' : 'transparent',
                boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                color: viewMode === 'grid' ? '#4f46e5' : '#64748b',
              }}
            >
              ▦ Grid View
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                background: viewMode === 'table' ? 'white' : 'transparent',
                boxShadow: viewMode === 'table' ? '0 2px 4px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                color: viewMode === 'table' ? '#4f46e5' : '#64748b',
              }}
            >
              ☰ Table View
            </button>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '14px' }}>
            <div style={{ fontSize: '36px', marginBottom: '15px' }}>⏳</div>
            <h3 style={{ margin: 0, color: '#334155' }}>Loading Clinical Worksheets...</h3>
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>Fetching library contents from server</p>
          </div>
        ) : filteredList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📂</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>No worksheets found</h3>
            <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '450px', margin: '0 auto 20px auto' }}>
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search query or filters to find worksheets.'
                : 'Get started by uploading your first clinical worksheet for therapists.'}
            </p>
            <button
              onClick={handleOpenCreateModal}
              style={{
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ➕ Upload New Worksheet
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {paginatedList.map((item) => {
              const badge = getFormatBadge(item.fileType);
              return (
                <div
                  key={item._id || item.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    padding: '22px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.03)';
                  }}
                >
                  <div>
                    {/* Header Badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <span
                        style={{
                          backgroundColor: badge.bg,
                          color: badge.color,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        {badge.icon} {badge.label}
                      </span>

                      <button
                        onClick={() => handleTogglePublish(item)}
                        style={{
                          background: item.isPublished ? '#ecfdf5' : '#fef2f2',
                          color: item.isPublished ? '#059669' : '#dc2626',
                          border: `1px solid ${item.isPublished ? '#a7f3d0' : '#fecaca'}`,
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                        title="Click to toggle visibility for registered therapists"
                      >
                        <span>{item.isPublished ? '🟢 Published' : '🟡 Draft'}</span>
                      </button>
                    </div>

                    {/* Title */}
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                      {item.title}
                    </h3>

                    {/* Description */}
                    {item.description ? (
                      <p
                        style={{
                          color: '#475569',
                          fontSize: '13px',
                          margin: '0 0 10px 0',
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description}
                      </p>
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic', margin: '0 0 10px 0' }}>
                        No description provided
                      </p>
                    )}

                    {/* References pill if present */}
                    {item.references && (
                      <div
                        style={{
                          marginBottom: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: '#475569',
                          maxWidth: '100%',
                        }}
                        title={item.references}
                      >
                        <span>📚</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Ref: {item.references}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer Meta & Actions */}
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '12px', marginBottom: '14px' }}>
                      <span>📦 {formatFileSize(item.fileSize)}</span>
                      <span>📥 {item.downloadCount || 0} Downloads</span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setPreviewItem(item)}
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          background: '#f8fafc',
                          color: '#334155',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        👁️ Preview
                      </button>

                      <a
                        href={getMediaUrl(item.fileUrl)}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: '8px 12px',
                          background: '#e0e7ff',
                          color: '#4338ca',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        title="Download file"
                      >
                        📥
                      </a>

                      <button
                        onClick={() => handleOpenEditModal(item)}
                        style={{
                          padding: '8px 12px',
                          background: '#f1f5f9',
                          color: '#0f172a',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                        title="Edit worksheet"
                      >
                        ✏️
                      </button>

                      <button
                        onClick={() => setDeleteModal({ isOpen: true, item, loading: false })}
                        style={{
                          padding: '8px 12px',
                          background: '#fee2e2',
                          color: '#b91c1c',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                        title="Delete worksheet"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table View */
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>WORKSHEET TITLE & DETAILS</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>FILE & SIZE</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>DOWNLOADS</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>STATUS</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600, textAlign: 'right' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {paginatedList.map((item) => {
                  const badge = getFormatBadge(item.fileType);
                  return (
                    <tr key={item._id || item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px 18px', maxWidth: '380px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>{item.title}</div>
                        {item.description && (
                          <div style={{ color: '#475569', fontSize: '12px', marginTop: '4px', lineHeight: 1.4 }}>
                            {item.description}
                          </div>
                        )}
                        {item.references && (
                          <div style={{ color: '#6366f1', fontSize: '11px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>📚 Ref:</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.references}
                            </span>
                          </div>
                        )}
                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                          Uploaded: {new Date(item.createdAt || Date.now()).toLocaleDateString()}
                        </div>
                      </td>
                      <td style={{ padding: '16px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ backgroundColor: badge.bg, color: badge.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
                            {item.fileType ? item.fileType.toUpperCase() : 'PDF'}
                          </span>
                          <span style={{ color: '#64748b' }}>{formatFileSize(item.fileSize)}</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 18px', fontWeight: 700, color: '#0f172a' }}>
                        📥 {item.downloadCount || 0}
                      </td>
                      <td style={{ padding: '16px 18px' }}>
                        <button
                          onClick={() => handleTogglePublish(item)}
                          style={{
                            background: item.isPublished ? '#ecfdf5' : '#fef2f2',
                            color: item.isPublished ? '#059669' : '#dc2626',
                            border: `1px solid ${item.isPublished ? '#a7f3d0' : '#fecaca'}`,
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {item.isPublished ? '🟢 Visible' : '🟡 Draft'}
                        </button>
                      </td>
                      <td style={{ padding: '16px 18px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                          <button
                            onClick={() => setPreviewItem(item)}
                            style={{ padding: '6px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}
                            title="Preview details"
                          >
                            👁️
                          </button>
                          <a
                            href={getMediaUrl(item.fileUrl)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ padding: '6px 10px', background: '#e0e7ff', color: '#4338ca', borderRadius: '6px', textDecoration: 'none' }}
                            title="Download document"
                          >
                            📥
                          </a>
                          <button
                            onClick={() => handleOpenEditModal(item)}
                            style={{ padding: '6px 10px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteModal({ isOpen: true, item, loading: false })}
                            style={{ padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer' }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}

        {/* ==================== CREATE & EDIT MODAL ==================== */}
        {isModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '18px',
                width: '100%',
                maxWidth: '680px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'scaleUp 0.25s ease',
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: '22px 28px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '18px',
                  borderTopRightRadius: '18px',
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                    {modalMode === 'create' ? '➕ Add New Clinical Worksheet' : '✏️ Edit Clinical Worksheet'}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                    Manage clinical worksheet information and references for registered therapists.
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '22px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSubmitForm} style={{ padding: '28px' }}>
                {modalError && (
                  <div
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      fontSize: '13px',
                      lineHeight: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span>⚠️</span>
                    <span>{modalError}</span>
                  </div>
                )}

                {/* Worksheet Name */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    Worksheet Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter worksheet name (e.g. CBT Thought Record, Daily Mood Tracker)"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '15px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Worksheet Description */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    Worksheet Description
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Enter a brief summary of the worksheet, clinical utility, or focus areas (e.g. 7-column thought record for identifying distortions and rational responses)..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                    }}
                  />
                </div>

                {/* Worksheet References */}
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    References / Clinical Citations
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Beck, A.T. (1979). Cognitive Therapy of Depression; Linehan, M.M. (2014) DBT Skills Training Manual; source DOI or clinical book/journal references..."
                    value={formData.references}
                    onChange={(e) => setFormData({ ...formData, references: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                    Optional: Add evidence-based literature, author citations, or clinical bibliography for therapists.
                  </span>
                </div>

                {/* Submit Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{
                      padding: '11px 22px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    style={{
                      padding: '11px 26px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: formSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    }}
                  >
                    {formSubmitting ? '⏳ Saving...' : modalMode === 'create' ? '🚀 Save Worksheet' : '💾 Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ==================== PREVIEW DETAILS MODAL ==================== */}
        {previewItem && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '18px',
                width: '100%',
                maxWidth: '650px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
            >
              <div
                style={{
                  padding: '22px 28px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '18px',
                  borderTopRightRadius: '18px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  📑 Worksheet Details & Therapist Preview
                </h3>
                <button
                  onClick={() => setPreviewItem(null)}
                  style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '14px' }}>
                  <span
                    style={{
                      background: previewItem.isPublished ? '#ecfdf5' : '#fef2f2',
                      color: previewItem.isPublished ? '#059669' : '#dc2626',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    {previewItem.isPublished ? '🟢 Visible to Registered Therapists' : '🟡 Hidden / Draft'}
                  </span>
                </div>

                <h2 style={{ margin: '0 0 16px 0', fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
                  {previewItem.title}
                </h2>

                {/* Description Box in Preview */}
                {previewItem.description && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Worksheet Description
                    </div>
                    <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.6 }}>
                      {previewItem.description}
                    </p>
                  </div>
                )}

                {/* References Box in Preview */}
                {previewItem.references && (
                  <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#4338ca', letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📚</span>
                      <span>References & Clinical Citations</span>
                    </div>
                    <p style={{ margin: 0, color: '#3730a3', fontSize: '13px', lineHeight: 1.6, fontStyle: 'italic' }}>
                      {previewItem.references}
                    </p>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', background: '#f8fafc', padding: '16px', borderRadius: '10px', marginBottom: '24px', fontSize: '13px' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>File Size:</span> <strong>{formatFileSize(previewItem.fileSize)}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Format:</span> <strong>{previewItem.fileType?.toUpperCase() || 'PDF'}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Downloads:</span> <strong>{previewItem.downloadCount || 0} times</strong>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setPreviewItem(null)}
                    style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Close
                  </button>
                  {previewItem.fileUrl && (
                    <a
                      href={getMediaUrl(previewItem.fileUrl)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: '10px 20px',
                        background: '#4f46e5',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span>📥</span>
                      <span>Download Worksheet</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== DELETE MODAL ==================== */}
        {deleteModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '440px',
                padding: '28px',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '14px' }}>🗑️</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '19px', fontWeight: 800 }}>
                Delete Worksheet?
              </h3>
              <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.5, margin: '0 0 24px 0' }}>
                Are you sure you want to delete "<strong>{deleteModal.item?.title}</strong>"? Registered therapists will no longer be able to access or download this tool.
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                  style={{
                    padding: '10px 20px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteModal.loading}
                  onClick={handleDeleteConfirm}
                  style={{
                    padding: '10px 22px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: deleteModal.loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminWorksheetsPage;
